import { apiFetch } from '@/shared/lib/apiClient'
import { supabase } from '@/shared/lib/supabase'
import type { RedactionHistoryItem, CustomSkill } from '../types'

export interface SaveHistoryPayload {
  originalText: string
  redactedText: string
  tone: string
  audience: string
  messageType: string
  skillId: string | null
  inputTokens: number | null
  outputTokens: number | null
}

export async function saveHistory(payload: SaveHistoryPayload): Promise<RedactionHistoryItem> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('redaction_history')
    .insert({
      user_id: session.user.id,
      original_text: payload.originalText,
      redacted_text: payload.redactedText,
      tone: payload.tone,
      audience: payload.audience,
      message_type: payload.messageType,
      skill_id: payload.skillId,
      input_tokens: payload.inputTokens,
      output_tokens: payload.outputTokens,
    })
    .select()
    .single()

  if (error) throw error
  return mapHistoryRow(data)
}

export async function fetchHistory(): Promise<RedactionHistoryItem[]> {
  const { data, error } = await supabase
    .from('redaction_history')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return (data ?? []).map(mapHistoryRow)
}

export async function deleteHistory(id: string): Promise<void> {
  const { error } = await supabase
    .from('redaction_history')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function fetchSkills(): Promise<CustomSkill[]> {
  const { data, error } = await supabase
    .from('custom_skills')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []).map(mapSkillRow)
}

export async function createSkill(
  skill: Omit<CustomSkill, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<CustomSkill> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('custom_skills')
    .insert({
      user_id: session.user.id,
      name: skill.name,
      description: skill.description,
      system_prompt: skill.systemPrompt,
      is_default: skill.isDefault,
    })
    .select()
    .single()

  if (error) throw error
  return mapSkillRow(data)
}

export async function updateSkill(id: string, updates: Partial<CustomSkill>): Promise<CustomSkill> {
  const { data, error } = await supabase
    .from('custom_skills')
    .update({
      name: updates.name,
      description: updates.description,
      system_prompt: updates.systemPrompt,
      is_default: updates.isDefault,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return mapSkillRow(data)
}

export async function deleteSkill(id: string): Promise<void> {
  const { error } = await supabase
    .from('custom_skills')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function fetchTokenUsage(): Promise<{ tokensUsed: number; date: string }> {
  return apiFetch('/usage')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapHistoryRow(row: any): RedactionHistoryItem {
  return {
    id: row.id,
    userId: row.user_id,
    originalText: row.original_text,
    redactedText: row.redacted_text,
    tone: row.tone,
    audience: row.audience,
    messageType: row.message_type,
    skillId: row.skill_id,
    inputTokens: row.input_tokens,
    outputTokens: row.output_tokens,
    createdAt: row.created_at,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSkillRow(row: any): CustomSkill {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    description: row.description,
    systemPrompt: row.system_prompt,
    isDefault: row.is_default,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}
